import { Fragment } from "react";
import { inServerText } from "@/components/network/StatusPill";
import { Polyline, CircleMarker, Tooltip } from "react-leaflet";
import { AlertTriangle, ChevronDown } from "lucide-react";

export const PATH_COLOR = "#E11D48";
const KL = { olt: "OLT", splitter: "Splitter", odc: "ODC", odp: "ODP" };
const xy = (n) => [n.latitude, n.longitude];

// Garis sorot jalur optik; hanya segmen yang kedua ujungnya berkoordinat.
export function OpticalPathLayer({ path }) {
  if (!path?.nodes?.length) return null;
  const n = path.nodes;
  return (
    <>
      {n.slice(1).map((b, i) => (n[i].has_coords && b.has_coords && !(n[i].latitude === b.latitude && n[i].longitude === b.longitude) ? (
        <Polyline key={`op-${i}`} positions={[xy(n[i]), xy(b)]} pathOptions={{ color: PATH_COLOR, weight: 6, opacity: 0.95, className: "optical-path" }}>
          <Tooltip sticky>Jalur optik: {n[i].kode} → {b.kode}{b.via ? ` (${b.via})` : ""}</Tooltip></Polyline>) : null))}
      {n.filter((x) => x.has_coords).map((x) => (
        <CircleMarker key={`opn-${x.kind}-${x.id}`} center={xy(x)} radius={14} interactive={false}
                      pathOptions={{ color: PATH_COLOR, weight: 3, fillOpacity: 0.08, className: "optical-path" }} />
      ))}
    </>
  );
}

export function OpticalPathList({ path, onSelect }) {
  if (!path) return <p className="text-xs text-[#6B7280]">Memuat jalur optik…</p>;
  if (path.error) return <p className="text-xs text-[#B91C1C]">Gagal memuat jalur optik.</p>;
  const pel = path.pelanggan || [];
  return (
    <section className="rounded border border-[#FECDD3] p-2 text-xs" data-testid="optical-path">
      <div className="mb-1 flex items-center gap-1 font-bold uppercase" style={{ color: PATH_COLOR }}>Jalur Optik (disorot di peta)</div>
      {!path.complete && <p className="mb-1 flex items-center gap-1 rounded bg-[#FFFBEB] px-1.5 py-1 text-[#B45309]" data-testid="optical-path-incomplete">
        <AlertTriangle className="h-3 w-3" />Jalur belum lengkap: tidak ada relasi aktif ke hulu setelah {path.nodes[0]?.kode || "-"}.</p>}
      {path.parent?.mismatch && <p className="mb-1 rounded bg-[#FFFBEB] px-1.5 py-1 text-[#B45309]" data-testid="optical-path-parent-mismatch">
        ODC induk tersimpan berbeda dari jalur splitter ({path.parent.derived_odc_kode || "seharusnya kosong"}). Akan diselaraskan saat relasi splitter berubah.</p>}
      {path.parent?.warning && <p className="mb-1 rounded bg-[#FFFBEB] px-1.5 py-1 text-[#B45309]" data-testid="optical-path-parent-warning">{path.parent.warning}</p>}
      <ol className="space-y-0.5" data-testid="optical-path-nodes">{path.nodes.map((n, i) => (
        <Fragment key={`${n.kind}-${n.id}`}>
          {i > 0 && <li className="flex items-center gap-1 pl-2 text-[11px] text-[#9CA3AF]"><ChevronDown className="h-3 w-3" />{n.via || ""}</li>}
          <li data-testid={`optical-path-node-${n.kode}`}>
            <button type="button" className="font-semibold text-[#0055FF] disabled:text-inherit" disabled={!onSelect || n.kind === "olt" && !onSelect}
                    onClick={() => onSelect?.({ kind: n.kind, id: n.id })}>{KL[n.kind]} {n.kode || "(terhapus)"}</button>
            {n.rasio && <span className="text-[#6B7280]"> ({n.rasio})</span>}
            {n.in_server ? <span className="text-[#334155]" data-testid={`optical-inserver-${n.kode}`}> · {inServerText(n.in_server)}{n.has_coords ? "" : " (tidak digambar)"}</span>
              : !n.has_coords && <span className="text-[#9CA3AF]"> · tanpa koordinat (tidak digambar)</span>}
          </li>
        </Fragment>))}</ol>
      <div className="mt-2 border-t pt-1" data-testid="optical-path-pelanggan">
        <b>Pelanggan ODP ({pel.length})</b>
        {pel.length ? <ul className="mt-0.5 max-h-32 overflow-y-auto">{pel.map((c) => (
          <li key={c.customer_id}><span className="font-mono-tab text-[#6B7280]">port {c.port}</span> {c.nama_customer || c.customer_id}</li>))}</ul>
          : <p className="text-[#6B7280]">Belum ada pelanggan.</p>}
      </div>
    </section>
  );
}
